import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useTrialStatus } from 'modules/Onboarding/hooks';
import { expiryTone } from './expiryTone';

/**
 * TrialBanner component displays trial status at the top of the dashboard
 *
 * Shows:
 * - Days remaining in trial for Declarants
 * - Upgrade CTA when trial is expiring soon (≤3 days)
 * - Nothing if no active trial or loading/error state
 */
const TrialBanner = () => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const { data: trialStatus, isLoading, error } = useTrialStatus();

  // Don't render if loading, error, or no active trial
  if (isLoading || error || !trialStatus?.hasActiveTrial) {
    return null;
  }

  const handleUpgradeClick = () => {
    history.push('/pricing');
  };

  // Urgency bands live in expiryTone so this banner and the Plan tab cannot
  // disagree about whether the same expiry date is urgent.
  const colors = expiryTone(trialStatus.daysRemaining);

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-md p-2 mx-2 mb-2 mt-0`}>
      <div className="flex items-center gap-2">
        <FontAwesomeIcon
          icon={faClock}
          className={`${colors.icon} text-xs shrink-0`}
        />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium ${colors.text} leading-tight`}>
            {t('common:trialBanner.daysLeft', {
              count: trialStatus.daysRemaining,
              defaultValue: '{{count}} day left in free trial',
              defaultValue_other: '{{count}} days left in free trial',
            })}
          </p>
          {trialStatus.daysRemaining <= 3 && (
            <button
              onClick={handleUpgradeClick}
              className="text-[10px] text-blue-600 hover:text-blue-700 font-medium underline"
            >
              {t('common:trialBanner.upgradeNow', 'Upgrade now')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
